import styled from '@emotion/styled'
import { theme } from 'styles/theme/hav-theme'
import { MenuItem, CircularProgress } from '@mui/material'
import { DivProps } from 'commons/types/htmlElements'
import { statusBgKey, statusBgs } from 'commons/styles/StatusBG'

interface ContainerStyled extends DivProps {
  fullWidth: boolean
}

export const Container = styled.div<ContainerStyled>`
  width: 100%;
  display: flex;
  flex-direction: row;
  height: 50px;
  background: ${theme.colors.neutral.white};
  box-shadow: ${theme.effects.boxShadow['rule-01']};
  align-self: stretch;
`

type MainTitleProps = {
  status: 'initial' | 'created' | 'manage' | 'ordered'
  fullWidth: boolean
  type: statusBgKey
}

export const MainTitle = styled.div<MainTitleProps>`
  height: 100%;

  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ status, type }) => {
    switch (status) {
      case 'manage':
        return `
          background: ${theme.colors.alert.success.light};
        `
      case 'ordered':
        return `
          background: ${statusBgs[type]};
        `
      default:
        return `
          background: ${theme.colors.alert.info.main};
        `
    }
  }}

  h2,
  h3 {
    color: ${({ status }) =>
      status === 'manage' ? theme.colors.neutral.black : theme.colors.neutral.white};
    margin: 0;
  }

  h2 {
    font-size: ${({ status }) =>
      status === 'ordered' ? theme.typography.sizes.xs : theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.md};
  }

  h3 {
    font-size: ${theme.typography.sizes.xs};
    font-weight: ${theme.typography.weight.sm};
  }
`

export const OrderRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.xm};
`
export const OrderType = styled.div`
  height: 100%;
  width: 100%;
  max-width: 600px;
  padding: ${theme.spacing.sm} ${theme.spacing.xm};
  width: fit-content;
  display: flex;
  flex-direction: column;

  h2,
  h3 {
    margin: 0;
  }

  h2 {
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.md};
    color: ${theme.colors.neutral.black};

    span {
      font-weight: ${theme.typography.weight.xsm};
    }
  }

  h3 {
    font-size: ${theme.typography.sizes['2xs']};

    color: ${theme.colors.neutral.black};
  }
`

export const OrderInfo = styled.div`
  height: 100%;
  width: 100%;
  max-width: 200px;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  width: fit-content;

  h2,
  h3 {
    margin: 0;
  }

  h2 {
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.md};
    color: ${theme.colors.neutral.black};
  }

  h3 {
    margin-top: ${theme.spacing.xs};
    font-size: ${theme.typography.sizes.sm};
    color: ${theme.colors.neutral.medium};
  }
`
export const OvInfo = styled.div`
  width: 100%;

  width: fit-content;
  display: flex;
  flex-direction: row;
  align-items: center;

  h2,
  h3,
  h4 {
    margin: 0;
  }

  h2 {
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.md};
    color: ${theme.colors.neutral.black};
    width: 60px;
  }

  h3 {
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.xsm};
    color: ${theme.colors.neutral.high};
  }

  h4 {
    font-size: ${theme.typography.sizes['2xs']};
    font-weight: ${theme.typography.weight.xsm};
    color: ${theme.colors.neutral.mediumhover};
  }

  gap: 8px;
`
export const OvWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: ${theme.spacing.xm};
`
export const Actions = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: ${theme.spacing.xl};
`

export const StyledMenuItem = styled(MenuItem)`
  color: ${theme.colors.primary.main};
`

type ActionButtonProps = {
  fullfill: boolean
  disabled?: boolean
  sapButton?: boolean
  globalLoading?: boolean
}

export const ActionButton = styled.button<ActionButtonProps>`
  background: ${({ disabled, fullfill }) =>
    fullfill
      ? disabled
        ? theme.colors.neutral.clear
        : theme.colors.primary.main
      : theme.colors.neutral.white};

  color: ${({ disabled, fullfill }) =>
    fullfill
      ? disabled
        ? theme.colors.neutral.lighthover
        : theme.colors.neutral.white
      : disabled
      ? theme.colors.neutral.lighthover
      : theme.colors.neutral.high};

  svg {
    path {
      fill: ${({ disabled, fullfill }) =>
        fullfill
          ? disabled
            ? theme.colors.neutral.lighthover
            : theme.colors.neutral.white
          : disabled
          ? theme.colors.neutral.lighthover
          : theme.colors.primary.main};
    }
  }
  border-radius: ${theme.shapes.borderRadius.sm};
  border: 0;

  font-weight: ${theme.typography.weight.lg};
  font-family: ${theme.typography.family.primary};
  font-size: ${theme.typography.sizes['3xs']};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: ${theme.spacing.xs};
  transition: all 0.3s ease-in-out;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  & + button {
    margin-left: ${theme.spacing.sm};
  }

  :hover {
    filter: ${({ disabled }) => (disabled ? 'none' : 'brightness(90%)')};
  }
  ${({ sapButton }) => sapButton && `color: ${theme.colors.neutral.white}`}

  cursor: ${({ globalLoading }) => (!globalLoading ? 'pointer' : 'wait')};
`

type CircularProgressProps = {
  fullfill: boolean
}

export const CircularProgressStyled = styled(CircularProgress)<CircularProgressProps>`
  padding: 0.2rem;
  color: ${({ fullfill }) =>
    fullfill ? theme.colors.neutral.white : theme.colors.primary.main};
`

export const HamburguerMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column nowrap;
  gap: ${theme.spacing.sm};
`
export const HamburguerMenuLabel = styled.span`
  font-size: ${theme.typography.sizes['3xs']};
  font-weight: ${theme.typography.weight.lg};

  color: ${theme.colors.primary.main};
`
